import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, User } from 'lucide-react';
import { PromotionPageLayout } from '@/components/layout/PromotionPageLayout';
import { PromotionModalHeader } from '@/components/layout/PromotionModalHeader';
import { PromotionModalFooter } from '@/components/layout/PromotionModalFooter';
import { ParentProfileForm } from './components/ParentProfileForm';
import { StudentProfileForm } from './components/StudentProfileForm';
import {
  makeEmptyKid,
  makeEmptyStudent,
  isPersonComplete,
  isStudentProfileComplete,
} from './helpers';
import { ROUTES } from '@/constants/routes';

export default function PromotionProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = location.state ?? {};

  useEffect(() => {
    if (!incoming.role) {
      navigate(ROUTES.promotionEnrollment, { replace: true });
    }
  }, [incoming.role, navigate]);

  const isParent = incoming.role === 'parent';
  const isStudent = incoming.role === 'student';

  const [kids, setKids] = useState(
    incoming.kids && incoming.kids.length > 0 ? incoming.kids : [makeEmptyKid()]
  );
  const [student, setStudent] = useState(
    incoming.student ?? makeEmptyStudent()
  );

  const profileComplete =
    (isParent && kids.every(isPersonComplete)) ||
    (isStudent && isStudentProfileComplete(student));

  function updateKid(id, next) {
    setKids((prev) => prev.map((k) => (k.id === id ? next : k)));
  }

  function addKid() {
    setKids((prev) => [...prev, makeEmptyKid()]);
  }

  function removeKid(id) {
    setKids((prev) =>
      prev.length > 1 ? prev.filter((k) => k.id !== id) : prev
    );
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!profileComplete) return;
    navigate(ROUTES.promotionAvailability, {
      state: {
        ...incoming,
        kids: isParent ? kids : null,
        student: isStudent ? student : null,
      },
    });
  }

  return (
    <PromotionPageLayout step={2} totalSteps={4} modal modalSize="lg">
      <PromotionModalHeader
        icon={isParent ? Users : User}
        title={
          isParent
            ? "Tell us about the kid(s) you're enrolling"
            : 'Tell us a bit about yourself'
        }
        subtitle={
          isParent
            ? 'Add one card per student. All fields required.'
            : 'All fields required so we can match you with the right group.'
        }
        backTo={ROUTES.promotionEnrollment}
        backState={{ ...incoming, kids, student }}
      />
      <form onSubmit={handleSubmit}>
        {isParent ? (
          <ParentProfileForm
            kids={kids}
            onUpdate={updateKid}
            onAdd={addKid}
            onRemove={removeKid}
          />
        ) : (
          <StudentProfileForm student={student} onChange={setStudent} />
        )}
        <PromotionModalFooter disabled={!profileComplete}>
          Continue
        </PromotionModalFooter>
      </form>
    </PromotionPageLayout>
  );
}